import {
  BlockReferenceSearchItemProps,
  ITextSearchQuery,
  OverrideSQuery,
} from '@rhizome/pgtypes';
import {
  BLOCK_REFERENCE_MENTION,
  convertDBRowToSlateNode,
} from '@rhizome/shared-slate-utils';
import { comboboxStore } from '@udecode/plate-combobox';
import { usePlateEditorState } from '@udecode/plate-core';
import { ELEMENT_MENTION } from '@udecode/plate-mention';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { createFetcher } from '../../utils/DataFetchers';
import DateDisplay from '../DateDisplay';
import { ReadOnlyDisplay } from './usedPlugins';

export const useBlockReferences = (
  noteId: string
): BlockReferenceSearchItemProps[] => {
  const currentComboboxText = comboboxStore?.get?.text() || '';
  const activeId = comboboxStore?.get?.activeId();
  const editor = usePlateEditorState(noteId);
  const currentBlock =
    editor?.children?.[editor?.selection?.anchor?.path?.[0] || 0];

  const inputChanged = useRef(false);
  const [blockReferences, setBlockReferences] = useState<
    BlockReferenceSearchItemProps[]
  >([]);
  useEffect(() => {
    inputChanged.current = true;
  }, [currentComboboxText]);
  useEffect(() => {
    const fetchInterval = window.setInterval(() => {
      if (inputChanged.current) {
        createFetcher<OverrideSQuery<ITextSearchQuery>>('TextSearch')({
          content_type: 'slate',
          fulltext: currentComboboxText,
        }).then((res) => {
          inputChanged.current = false;
          const blockReferenceArray = res.reduce<
            BlockReferenceSearchItemProps[]
          >((acc, el) => {
            if (el.block_id !== currentBlock?.id) {
              return [
                ...acc,
                {
                  key: el.block_id,
                  value: el,
                  text: currentComboboxText,
                  query: currentComboboxText,
                },
              ];
            }
            return acc;
          }, []);

          setBlockReferences(
            activeId === BLOCK_REFERENCE_MENTION && currentComboboxText && res
              ? blockReferenceArray
              : []
          );
        });
      }
    }, 200);
    return () => {
      window.clearInterval(fetchInterval);
    };
  }, [activeId, currentBlock?.id, currentComboboxText]);

  return blockReferences;
};

export const RenderBlockReferences = (props: {
  item: BlockReferenceSearchItemProps;
}) => {
  const { item } = props;
  const title = item?.value?.note_title || 'untitled';

  return (
    <div>
      <TitleText>{"from '" + title + "'"}</TitleText>
      <SubtitleText>
        <DateDisplay
          created={new Date(item?.value?.creation_date)}
          lastEdited={new Date(item?.value?.last_edited_date)}
          default="lastEdited"
        />
      </SubtitleText>
      <ReadOnlyDisplay
        nodes={item?.value ? [convertDBRowToSlateNode(item.value)] : []}
        searchTerm={item?.query}
        allowPreviews={false}
        isRoot={false}
      />
    </div>
  );
};

export const useTagsCombobox = (autocompleteTags: string[]) => {
  const currentComboboxText = comboboxStore?.get?.text();
  const activeId = comboboxStore?.get?.activeId();
  const formattedMentions = useMemo(() => {
    return autocompleteTags.map((item) => {
      return {
        key: item,
        value: item,
        text: item,
      };
    });
  }, [autocompleteTags]);

  const tagComboboxItems =
    activeId === ELEMENT_MENTION && currentComboboxText
      ? [
          {
            key: currentComboboxText + 'new',
            value: currentComboboxText,
            text: currentComboboxText,
          },
          ...formattedMentions,
        ]
      : formattedMentions;
  return tagComboboxItems;
};

const SubtitleText = styled.div`
  padding: 0px 0px 0px 0px;
  margin: 0px;
  font-style: italic;
  font-size: 0.75rem;
`;

const TitleText = styled.p`
  padding: 0px 0px 0px 0px;
  margin: 0px;
  font-size: 1.5rem;
  display: inline-block;
`;
